import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import toast from 'react-hot-toast';

import { useEventListener } from 'src/hooks/use-event-listener';
import { getLocalStorageValue, setLocalStorageValue } from 'src/utils/utilsLocalStorage';

// ----------------------------------------------------------------------

const DEBUG_MODE_STORAGE_KEY = 'cheddar.debugMode';
const CHORD_WINDOW_MS = 1000;

type DebugModeContextValue = {
  debugMode: boolean;
  toggleDebugMode: () => void;
};

const DebugModeContext = createContext<DebugModeContextValue | undefined>(undefined);

export function DebugModeProvider({ children }: { children: ReactNode }) {
  // Annotated because getLocalStorageValue infers its generic from the default, so a bare
  // `false` narrows the state to the literal type and toggling it stops compiling.
  const [debugMode, setDebugMode] = useState<boolean>(() =>
    getLocalStorageValue(DEBUG_MODE_STORAGE_KEY, false),
  );
  const awaitingMRef = useRef(false);
  const chordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearChord = useCallback(() => {
    awaitingMRef.current = false;
    if (chordTimerRef.current) {
      clearTimeout(chordTimerRef.current);
      chordTimerRef.current = null;
    }
  }, []);

  const toggleDebugMode = useCallback(() => {
    setDebugMode((prev) => {
      const next = !prev;
      setLocalStorageValue(DEBUG_MODE_STORAGE_KEY, next);
      toast.success(next ? 'Debug mode on' : 'Debug mode off');
      return next;
    });
  }, []);

  useEventListener('keydown', (event) => {
    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    const key = event.key.toLowerCase();

    if (event.ctrlKey && key === 'd') {
      event.preventDefault();
      clearChord();
      awaitingMRef.current = true;
      chordTimerRef.current = setTimeout(clearChord, CHORD_WINDOW_MS);
      return;
    }

    if (awaitingMRef.current && key === 'm') {
      event.preventDefault();
      clearChord();
      toggleDebugMode();
      return;
    }

    if (awaitingMRef.current && key !== 'control' && key !== 'meta' && key !== 'alt' && key !== 'shift') {
      clearChord();
    }
  });

  const value = useMemo(
    () => ({ debugMode, toggleDebugMode }),
    [debugMode, toggleDebugMode],
  );

  return <DebugModeContext.Provider value={value}>{children}</DebugModeContext.Provider>;
}

export function useDebugMode() {
  const context = useContext(DebugModeContext);
  if (!context) {
    throw new Error('useDebugMode must be used within a DebugModeProvider');
  }
  return context;
}
